import { useState, useEffect, useCallback } from 'react'
import { adminApi } from '@/services/adminApi'
import { ChevronDown, Loader2, CheckCircle2, AlertCircle, Save, RefreshCw, Play, Clock, DollarSign, Brain } from 'lucide-react'
import { toast } from 'sonner'

const MODEL_TYPES = [
  { key: 'chat',                      label: 'Chat',                        group: 'Core' },
  { key: 'triage',                    label: 'Triage',                      group: 'Core' },
  { key: 'agent',                     label: 'Agent',                       group: 'Core' },
  { key: 'suggestiondetect',          label: 'Suggestion Detection',        group: 'Core' },
  { key: 'subagent',                  label: 'Sub Agent',                   group: 'Core' },
  { key: 'instruction',               label: 'Instruction',                 group: 'Core' },
  { key: 'vision',                    label: 'Vision',                      group: 'Vision & Media' },
  { key: 'image',                     label: 'Image Generation',            group: 'Vision & Media' },
  { key: 'workflow',                  label: 'Workflow',                    group: 'Workflow & Tools' },
  { key: 'dynamictools',              label: 'Dynamic Tools',               group: 'Workflow & Tools' },
  { key: 'browseragent',              label: 'Browser Agent',               group: 'Workflow & Tools' },
  { key: 'tooloutputsummarizer',      label: 'Tool Output Summarizer',      group: 'Workflow & Tools' },
  { key: 'slidecontent',              label: 'Slides — Content & Plan',     group: 'Slides' },
  { key: 'slidestructured',           label: 'Slides — Structured & Theme', group: 'Slides' },
  { key: 'title',                     label: 'Title Generation',            group: 'Content' },
  { key: 'summary',                   label: 'Summary',                     group: 'Content' },
  { key: 'metadata',                  label: 'Metadata Generation',         group: 'Content' },
  { key: 'memory',                    label: 'Memory',                      group: 'Content' },
  { key: 'bichat',                    label: 'BI Chat',                     group: 'Data' },
  { key: 'postgresql',                label: 'PostgreSQL',                  group: 'Data' },
  { key: 'mysql',                     label: 'MySQL',                       group: 'Data' },
  { key: 'typesense',                 label: 'Typesense',                   group: 'Data' },
  { key: 'vectorsearchsummarization', label: 'Vector Search Summarization', group: 'Data' },
  { key: 'libraappsplan',             label: 'Libra Apps — Plan',           group: 'Libra Apps' },
  { key: 'libraappsbuild',            label: 'Libra Apps — Build',          group: 'Libra Apps' },
  { key: 'preview',                   label: 'Preview Author',              group: 'Libra Apps' },
  { key: 'meetingprep',               label: 'Meeting Prep — Synthesizer',  group: 'Background Jobs' },
  { key: 'meetingprepanalyzer',       label: 'Meeting Prep — Analyzer',     group: 'Background Jobs' },
  { key: 'ambientmeeting',            label: 'Ambient Meetings',            group: 'Background Jobs' },
  { key: 'recallsummary',             label: 'Online Meetings — Summary',   group: 'Background Jobs' },
  { key: 'weekahead',                 label: 'Week Ahead',                  group: 'Background Jobs' },
  { key: 'webhooktodo',               label: 'Webhook Todo',                group: 'Misc' },
]

const GROUPS = ['Core', 'Vision & Media', 'Workflow & Tools', 'Slides', 'Content', 'Data', 'Libra Apps', 'Background Jobs', 'Misc']

interface LLMModel {
  id: string
  name: string
  displayName: string | null
  providerId: string
  provider: { id: string; name: string }
}

interface RowState {
  providerId: string
  providerName: string
  modelName: string
  dirty: boolean
  saving: boolean
  saved: boolean
  error: string | null
}

interface TestState {
  status: 'idle' | 'running' | 'ok' | 'error'
  response?: string
  latencyMs?: number
  error?: string
}

interface AgentConfigSummary {
  totalOrgs: number
  enableInstructions: number
  enableToolOutputSummarization: number
  AIResponseMemory: number
}

type Tab = 'config' | 'test' | 'agent'

const Select = ({ value, onChange, disabled, placeholder, children }: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  placeholder: string
  children: React.ReactNode
}) => (
  <div className="relative">
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      className="w-full appearance-none rounded-lg border border-gray-200 bg-white px-3 py-2 pr-7 text-sm text-gray-800 outline-none focus:border-gray-400 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
    >
      <option value="">{placeholder}</option>
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
  </div>
)

export default function GlobalModelConfigPage() {
  const [tab, setTab] = useState<Tab>('config')
  const [llmModels, setLlmModels] = useState<LLMModel[]>([])
  const [config, setConfig] = useState<Record<string, RowState>>({})
  const [loading, setLoading] = useState(true)
  const [savingAll, setSavingAll] = useState(false)
  const [testType, setTestType] = useState('')
  const [testState, setTestState] = useState<TestState>({ status: 'idle' })
  const [agentSummary, setAgentSummary] = useState<AgentConfigSummary | null>(null)
  const [agentSaving, setAgentSaving] = useState<string | null>(null)
  const [imagePricings, setImagePricings] = useState<{ model: string; quality: string; size: string; costUsd: number }[] | null>(null)
  const [imagePricingExpanded, setImagePricingExpanded] = useState(false)

  const providers = Array.from(
    new Map(llmModels.map(m => [m.providerId, m.provider])).values()
  ).sort((a, b) => a.name.localeCompare(b.name))

  const modelsFor = useCallback(
    (providerId: string) =>
      llmModels.filter(m => m.providerId === providerId)
        .sort((a, b) => (a.displayName ?? a.name).localeCompare(b.displayName ?? b.name)),
    [llmModels]
  )

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [models, global] = await Promise.all([
        adminApi.getAllLLMModels(),
        adminApi.getGlobalModelConfig(),
      ])
      setLlmModels(models)
      const nameToId = new Map(models.map((m: LLMModel) => [m.provider.name, m.providerId]))
      const init: Record<string, RowState> = {}
      for (const t of MODEL_TYPES) {
        const cur = (global as any)[t.key] ?? { model: null, provider: null }
        const providerId = cur.provider ? (nameToId.get(cur.provider) ?? '') : ''
        init[t.key] = { providerId, providerName: cur.provider ?? '', modelName: cur.model ?? '', dirty: false, saving: false, saved: false, error: null }
      }
      setConfig(init)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadAgentSummary = useCallback(async () => {
    try {
      const summary = await adminApi.getAgentConfigSummary()
      setAgentSummary(summary)
    } catch (e) {
      console.error('Failed to load agent config summary:', e)
    }
  }, [])

  const handleBulkAgentToggle = async (
    field: 'enableInstructions' | 'enableToolOutputSummarization' | 'AIResponseMemory',
    value: boolean
  ) => {
    setAgentSaving(field)
    try {
      await adminApi.bulkUpdateAgentConfig({ [field]: value })
      toast.success(`Updated for all organizations`)
      await loadAgentSummary()
    } catch (e) {
      console.error('Failed to bulk update agent config:', e)
      toast.error('Failed to update agent configuration')
    } finally {
      setAgentSaving(null)
    }
  }

  useEffect(() => { load() }, [load])
  useEffect(() => { loadAgentSummary() }, [loadAgentSummary])

  const fetchImagePricings = useCallback(() => {
    if (imagePricings !== null) return
    adminApi.getImageModelPricings().then(data => setImagePricings(data)).catch(() => setImagePricings([]))
  }, [imagePricings])

  const patch = (key: string, p: Partial<RowState>) =>
    setConfig(prev => ({ ...prev, [key]: { ...prev[key], ...p } }))

  const onProvider = (key: string, providerId: string) => {
    const prov = providers.find(p => p.id === providerId)
    patch(key, { providerId, providerName: prov?.name ?? '', modelName: '', dirty: true, saved: false, error: null })
  }

  const onModel = (key: string, modelName: string) =>
    patch(key, { modelName, dirty: true, saved: false, error: null })

  const save = async (key: string) => {
    const row = config[key]
    if (!row.providerId || !row.modelName) return
    patch(key, { saving: true, error: null })
    try {
      await adminApi.setModelForAllOrganizations({ model: row.modelName, providerId: row.providerId, type: key as any })
      patch(key, { saving: false, dirty: false, saved: true })
      setTimeout(() => patch(key, { saved: false }), 2500)
    } catch (e: any) {
      patch(key, { saving: false, error: e?.response?.data?.error ?? 'Failed' })
    }
  }

  const saveAll = async () => {
    const dirty = MODEL_TYPES.filter(t => config[t.key]?.dirty && config[t.key]?.providerId && config[t.key]?.modelName)
    if (!dirty.length) return
    setSavingAll(true)
    await Promise.allSettled(dirty.map(t => save(t.key)))
    setSavingAll(false)
  }

  const runTest = async () => {
    if (!testType) return
    setTestState({ status: 'running' })
    try {
      const res = await adminApi.testModel(testType)
      setTestState({ status: 'ok', response: res.response, latencyMs: res.latencyMs })
    } catch (e: any) {
      const raw = e?.response?.data?.error
      const msg = typeof raw === 'string' ? raw : (e?.message ?? 'Test failed')
      setTestState({ status: 'error', error: msg })
    }
  }

  const dirtyCount = MODEL_TYPES.filter(t => config[t.key]?.dirty && config[t.key]?.providerId && config[t.key]?.modelName).length
  const configuredCount = MODEL_TYPES.filter(t => config[t.key]?.modelName && !config[t.key]?.dirty).length

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
      </div>
    )
  }

  return (
    <div className="px-8 py-10 pb-28">
      {/* Header */}
      <div className="mb-8 flex items-end justify-between border-b border-gray-100 pb-6">
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">System</p>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Agent Configuration</h1>
          <p className="mt-1 text-sm text-gray-400">{configuredCount}/{MODEL_TYPES.length} types configured</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500 hover:bg-gray-50">
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-1 border-b border-gray-100">
        {(['config', 'test', 'agent'] as Tab[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
              tab === t
                ? 'border-b-2 border-gray-900 text-gray-900'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {t === 'config' ? 'Models' : t === 'test' ? 'Test' : 'Agent Config'}
          </button>
        ))}
      </div>

      {/* Config Tab */}
      {tab === 'config' && (
        <div className="space-y-6">
          {GROUPS.map(group => {
            const types = MODEL_TYPES.filter(t => t.group === group)
            return (
              <div key={group}>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{group}</p>
                <div className="overflow-hidden rounded-xl border border-gray-100">
                  <table className="w-full">
                    <tbody className="divide-y divide-gray-50">
                      {types.map(type => {
                        const row = config[type.key]
                        if (!row) return null
                        const isSet = !!row.modelName && !row.dirty
                        const imageModelPrices = type.key === 'image' && row.modelName && imagePricings
                          ? imagePricings.filter(p => p.model === row.modelName)
                          : []
                        return (
                          <tr key={type.key} className="bg-white">
                            <td className="w-44 px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${isSet ? 'bg-emerald-400' : row.dirty && row.modelName ? 'bg-amber-400' : 'bg-gray-200'}`} />
                                <span className="text-sm text-gray-700">{type.label}</span>
                              </div>
                              {type.key === 'image' && row.modelName && (
                                <div className="mt-2 ml-3.5">
                                  <button
                                    onClick={() => { fetchImagePricings(); setImagePricingExpanded(!imagePricingExpanded) }}
                                    className="flex items-center gap-1 text-[10px] font-medium text-purple-600 hover:text-purple-800"
                                  >
                                    <DollarSign className="h-3 w-3" />
                                    {imageModelPrices.length > 0 ? `${imageModelPrices.length} price tiers` : 'View pricing'}
                                    <ChevronDown className={`h-3 w-3 transition-transform ${imagePricingExpanded ? 'rotate-180' : ''}`} />
                                  </button>
                                  {imagePricingExpanded && (
                                    <div className="mt-1.5 rounded-md border border-gray-100 bg-gray-50 p-2 text-[10px]">
                                      {imageModelPrices.length > 0 ? (
                                        <>
                                          <div className="grid grid-cols-3 gap-x-3 gap-y-0.5 font-medium text-gray-500 mb-1">
                                            <span>Quality</span><span>Size</span><span>Cost</span>
                                          </div>
                                          {imageModelPrices.map((p, i) => (
                                            <div key={i} className="grid grid-cols-3 gap-x-3 gap-y-0.5 text-gray-700">
                                              <span>{p.quality}</span>
                                              <span className="font-mono">{p.size}</span>
                                              <span>${p.costUsd.toFixed(3)}</span>
                                            </div>
                                          ))}
                                        </>
                                      ) : (
                                        <span className="text-amber-600">No pricing configured for this model</span>
                                      )}
                                      <a href="/admin/model-pricing" className="mt-1.5 block text-purple-600 hover:underline">
                                        Manage pricing →
                                      </a>
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-3 w-48">
                              <Select value={row.providerId} onChange={v => onProvider(type.key, v)} placeholder="Provider">
                                {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                              </Select>
                            </td>
                            <td className="px-2 py-3">
                              <Select value={row.modelName} onChange={v => onModel(type.key, v)} placeholder={row.providerId ? 'Model' : '—'} disabled={!row.providerId}>
                                {modelsFor(row.providerId).map(m => <option key={m.id} value={m.name}>{m.displayName ?? m.name}</option>)}
                              </Select>
                            </td>
                            <td className="w-24 px-4 py-3 text-right">
                              {row.saving ? (
                                <Loader2 className="ml-auto h-4 w-4 animate-spin text-gray-400" />
                              ) : row.saved ? (
                                <div className="flex items-center justify-end gap-1 text-xs text-emerald-600">
                                  <CheckCircle2 className="h-3.5 w-3.5" /> Saved
                                </div>
                              ) : row.error ? (
                                <span className="text-xs text-red-500 cursor-pointer" title={row.error} onClick={() => patch(type.key, { error: null })}>
                                  Error
                                </span>
                              ) : row.dirty && row.providerId && row.modelName ? (
                                <button onClick={() => save(type.key)} className="rounded-lg bg-gray-900 px-3 py-1 text-xs font-medium text-white hover:bg-gray-700">
                                  Apply
                                </button>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Test Tab */}
      {tab === 'test' && (
        <div className="max-w-lg space-y-6">
          <div>
            <p className="mb-3 text-sm text-gray-500">
              Select a model type and run a test call to verify it responds correctly with the current environment configuration.
            </p>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <select
                  value={testType}
                  onChange={e => { setTestType(e.target.value); setTestState({ status: 'idle' }) }}
                  className="w-full appearance-none rounded-lg border border-gray-200 bg-white px-3 py-2.5 pr-7 text-sm text-gray-800 outline-none focus:border-gray-400"
                >
                  <option value="">Select model type…</option>
                  {MODEL_TYPES.map(t => (
                    <option key={t.key} value={t.key}>
                      {t.label} {config[t.key]?.modelName ? `· ${config[t.key].modelName}` : '(not configured)'}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              </div>
              <button
                onClick={runTest}
                disabled={!testType || testState.status === 'running'}
                className="flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-40"
              >
                {testState.status === 'running' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
                Run
              </button>
            </div>
          </div>

          {testState.status === 'ok' && (
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" />
                  Model responded
                </div>
                <div className="flex items-center gap-1 text-xs text-emerald-600">
                  <Clock className="h-3 w-3" />
                  {testState.latencyMs}ms
                </div>
              </div>
              <p className="font-mono text-xs text-emerald-800">"{testState.response}"</p>
            </div>
          )}

          {testState.status === 'error' && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-4">
              <div className="mb-1 flex items-center gap-1.5 text-sm font-medium text-red-700">
                <AlertCircle className="h-4 w-4" />
                Test failed
              </div>
              <p className="text-xs text-red-600">{testState.error}</p>
            </div>
          )}
        </div>
      )}

      {/* Agent Config Tab */}
      {tab === 'agent' && (
        <div className="max-w-2xl space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Brain className="h-5 w-5 text-gray-700" />
              <h2 className="text-lg font-semibold text-gray-900">Agent Configuration</h2>
            </div>
            <p className="text-sm text-gray-500">
              Toggle settings for all organizations at once.
              {agentSummary && (
                <span className="text-gray-400"> ({agentSummary.totalOrgs} organizations total)</span>
              )}
            </p>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-100">
            {[
              {
                field: 'enableInstructions' as const,
                label: 'Enable Instructions',
                description: 'Allow agents to use custom instructions and system prompts to guide their behavior and responses.',
                enabled: agentSummary?.enableInstructions ?? 0,
              },
              {
                field: 'AIResponseMemory' as const,
                label: 'Process AI Responses for Memory',
                description: 'When enabled, memory extraction will process both user messages and AI assistant responses.',
                enabled: agentSummary?.AIResponseMemory ?? 0,
              },
              {
                field: 'enableToolOutputSummarization' as const,
                label: 'Enable Tool Output Summarization',
                description: 'Automatically summarize large tool outputs using a smaller model to reduce token usage and improve response times.',
                enabled: agentSummary?.enableToolOutputSummarization ?? 0,
              },
            ].map(item => (
              <div key={item.field} className="flex items-center justify-between px-5 py-4 bg-white border-b border-gray-50 last:border-b-0">
                <div className="flex-1 pr-6">
                  <p className="text-sm font-medium text-gray-800">{item.label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{item.description}</p>
                  {agentSummary && (
                    <p className="text-xs text-gray-400 mt-1">
                      {item.enabled}/{agentSummary.totalOrgs} orgs enabled
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {agentSaving === item.field && (
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  )}
                  <button
                    onClick={() => handleBulkAgentToggle(item.field, true)}
                    disabled={agentSaving === item.field}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-700 disabled:opacity-40"
                  >
                    Enable All
                  </button>
                  <button
                    onClick={() => handleBulkAgentToggle(item.field, false)}
                    disabled={agentSaving === item.field}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-red-50 hover:border-red-200 hover:text-red-700 disabled:opacity-40"
                  >
                    Disable All
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sticky save bar */}
      {tab === 'config' && dirtyCount > 0 && (
        <div className="fixed bottom-0 left-64 right-0 border-t border-gray-100 bg-white/95 px-8 py-4 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              <span className="font-medium text-gray-900">{dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}</span>
            </p>
            <button
              onClick={saveAll}
              disabled={savingAll}
              className="flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-50"
            >
              {savingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save all
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
